<script lang="ts" module>
  // Jeu d'icônes unique de l'app (refonte visuelle, août 2026).
  //
  // Un seul style : SVG au trait `currentColor`, `stroke-width: 1.5`,
  // `linecap`/`linejoin` ronds, dessiné sur une grille de 16 (interface)
  // ou de 20 (rail). Ça remplace le mélange d'emoji (📎 ⟳ ◐ ☰ ⇋ ★ ☆ ✦
  // 🔔 📋 🗑 ✗ ✓ ×) et de SVG ad-hoc qu'on avait avant : les emoji ne
  // suivent ni `currentColor` ni la graisse du trait, donc ils ne
  // pouvaient pas participer aux états de survol.
  //
  // Les tracés viennent des maquettes du handoff — ne pas les redessiner
  // à la main, les reprendre tels quels.
  export type IconName = keyof typeof PATHS;

  const G20 = new Set([
    "rail-inbox", "rail-crm", "rail-tasks", "rail-tracking",
  ]);

  const PATHS = {
    // — interface, grille 16 —
    search: '<path d="M10 10l4 4M11.5 7A4.5 4.5 0 1 1 2.5 7a4.5 4.5 0 0 1 9 0Z"/>',
    bell: '<path d="M3.5 11h9l-1.2-1.6V6.5a3.3 3.3 0 0 0-2.6-3.2V2.5a.7.7 0 1 0-1.4 0v.8A3.3 3.3 0 0 0 4.7 6.5V9.4L3.5 11ZM6.6 12.5a1.4 1.4 0 0 0 2.8 0"/>',
    plus: '<path d="M8 3.5v9M3.5 8h9"/>',
    minus: '<path d="M3.5 8h9"/>',
    filter: '<path d="M2.5 4.5h11M4.5 8h7M6.5 11.5h3"/>',
    refresh: '<path d="M13.5 4.5V1.8m0 0H10.8m2.7 0A6 6 0 1 0 14 8"/>',
    eye: '<circle cx="8" cy="8" r="2"/><path d="M1.5 8C3 5 5.4 3.5 8 3.5s5 1.5 6.5 4.5c-1.5 3-3.9 4.5-6.5 4.5S3 11 1.5 8Z"/>',
    paperclip: '<path d="M12 6 7.3 10.8a2 2 0 1 1-2.8-2.8l5.5-5.5a3.3 3.3 0 0 1 4.6 4.7l-6.4 6.4a4.6 4.6 0 1 1-6.5-6.5l5.7-5.7"/>',
    star: '<path d="m8 1.6 1.95 4 4.4.6-3.18 3.1.75 4.4L8 11.6l-3.92 2.1.75-4.4L1.65 6.2l4.4-.6Z"/>',
    "chevron-left": '<path d="M9.5 3.5 5 8l4.5 4.5"/>',
    "chevron-right": '<path d="M6.5 3.5 11 8l-4.5 4.5"/>',
    "chevron-down": '<path d="M4 6l4 4 4-4"/>',
    reply: '<path d="M6 4 2.5 7.5 6 11"/><path d="M2.5 7.5h7a4 4 0 0 1 4 4v1"/>',
    "reply-all": '<path d="M5 3.5 1.5 7 5 10.5M9 3.5 5.5 7 9 10.5"/><path d="M5.5 7h5a3.5 3.5 0 0 1 3.5 3.5v1.5"/>',
    forward: '<path d="M10 4l3.5 3.5L10 11"/><path d="M13.5 7.5h-7A4 4 0 0 0 2.5 11.5v1"/>',
    archive: '<rect x="2" y="3" width="12" height="3" rx="0.8"/><path d="M3 6.5v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-6M6.5 9.5h3"/>',
    trash: '<path d="M3 4.5h10M6 4.5V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5M4.5 4.5v8a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1v-8"/>',
    more: '<circle cx="4" cy="8" r="1.3"/><circle cx="8" cy="8" r="1.3"/><circle cx="12" cy="8" r="1.3"/>',
    close: '<path d="M4.5 4.5l7 7M11.5 4.5l-7 7"/>',
    mail: '<path d="M2 4h12v8H2z"/><path d="M2 4l6 5 6-5"/>',
    table: '<rect x="2" y="3" width="12" height="10" rx="1.5"/><path d="M2 6.5h12M6 6.5v6.5"/>',
    ai: '<path d="M8 2l1.3 3.4L12.7 7 9.3 8.3 8 11.7 6.7 8.3 3.3 7l3.4-1.3z"/><path d="M12.8 11l.4 1.1 1.1.4-1.1.4-.4 1.1-.4-1.1-1.1-.4 1.1-.4z"/>',
    check: '<path d="M3 8.5 6.2 12 13 4.5"/>',
    list: '<path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h7"/>',
    pencil: '<path d="M11.5 2.5l2 2L5 13H3v-2l8.5-8.5z"/>',
    settings: '<circle cx="8" cy="8" r="2.2"/><path d="M8 1.6v1.7M8 12.7v1.7M14.4 8h-1.7M3.3 8H1.6M12.5 3.5l-1.2 1.2M4.7 11.3l-1.2 1.2M12.5 12.5l-1.2-1.2M4.7 4.7 3.5 3.5"/>',
    theme: '<circle cx="8" cy="8" r="5.5"/><path d="M8 2.5v11" /><path d="M8 3.6a4.4 4.4 0 0 1 0 8.8z" fill="currentColor" stroke="none"/>',
    clock: '<circle cx="8" cy="8" r="6"/><path d="M8 4.5V8l2.5 1.5"/>',
    download: '<path d="M8 2.5v8M4.5 7.5 8 11l3.5-3.5"/><path d="M2.5 13.2h11"/>',
    external: '<path d="M9 3h4v4M13 3l-5.5 5.5"/><path d="M11 9v3a1.5 1.5 0 0 1-1.5 1.5h-6A1.5 1.5 0 0 1 2 12V6a1.5 1.5 0 0 1 1.5-1.5h3"/>',
    code: '<path d="M5.5 4.5 2 8l3.5 3.5M10.5 4.5 14 8l-3.5 3.5M9.4 2.8 6.6 13.2"/>',

    // — rail, grille 20 —
    "rail-inbox": '<path d="M2.5 11.5h4l1.2 2h4.6l1.2-2h4"/><path d="M2.5 11.5 5 4.5h10l2.5 7v4a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1z"/>',
    "rail-crm": '<circle cx="10" cy="7" r="3"/><path d="M4 17c0-3.3 2.7-6 6-6s6 2.7 6 6"/>',
    "rail-tasks": '<path d="M3 6.5l1.6 1.6L7.5 5"/><path d="M10 6.5h7M3 13l1.6 1.6L7.5 11.5M10 13h7"/>',
    "rail-tracking": '<path d="M3 15l4-5 3.5 3L17 6"/><path d="M13 6h4v4"/>',
  } as const;
</script>

<script lang="ts">
  let {
    name,
    size = 16,
    filled = false,
    strokeWidth = 1.5,
    class: klass = "",
  }: {
    name: IconName;
    size?: number;
    /** Remplit le tracé (étoile suivie, œil plein…) au lieu de le laisser au trait. */
    filled?: boolean;
    strokeWidth?: number;
    class?: string;
  } = $props();

  let box = $derived(G20.has(name) ? 20 : 16);
</script>

<svg
  class={klass}
  viewBox="0 0 {box} {box}"
  width={size}
  height={size}
  fill={filled ? "currentColor" : "none"}
  stroke="currentColor"
  stroke-width={strokeWidth}
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
  focusable="false"
>{@html PATHS[name]}</svg>

<style>
  svg { flex-shrink: 0; display: block; }
</style>
